import React, { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import VoiceDictationButton from "../components/VoiceDictationButton";
import AIAnalysisPreview from "../components/AIAnalysisPreview";
import CaseTracker from "../components/CaseTracker";
import { INDIAN_STATES, INDIAN_LANGUAGES } from "../lib/constants";
import { COUNTRIES as ALL_COUNTRIES, countryByIso, feePreview } from "../lib/countries";
import { toast } from "sonner";
import { ga } from "../lib/analytics";
import {
  Upload, FileText, Stethoscope, Clock, CheckCircle2, CreditCard,
  Zap, Smartphone, Star, ChevronLeft, ChevronRight, Search, BadgeCheck, IndianRupee, Briefcase, Users, ShieldCheck,
  Sparkles, X, ArrowRight,
} from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
// (Dialog import removed — UPI QR dialog deleted)


// Fallback avatar generator — deterministic initials avatars via DiceBear
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const avatarFor = (doc) => {
  if (doc?.photo_url) {
    // photo_url is stored as a relative path (e.g. /api/doctors/xxx/photo?v=123).
    // Prepend REACT_APP_BACKEND_URL so the <img> resolves correctly from any host
    // (PWA standalone, service-worker fallback, Android WebView, etc).
    return doc.photo_url.startsWith("http") ? doc.photo_url : `${BACKEND}${doc.photo_url}`;
  }
  const seed = encodeURIComponent(doc?.name || doc?.id || "NO");
  // DiceBear "initials" style — same name always gets the same colour, fast CDN, no auth
  return `https://api.dicebear.com/7.x/initials/svg?seed=${seed}&backgroundType=gradientLinear&fontWeight=600&chars=2`;
};

// Map condition slugs (from NeuroBot / condition pages) → symptom-tag chips on
// step 2. Slugs that don't have a single tag map fall through cleanly.
const SLUG_TO_TAG = {
  "brain-tumor":           "Headache",   // typical presenting symptom
  "stroke":                "Weakness",
  "epilepsy":              "Seizure",
  "spine-back-pain":       "Numbness",
  "headache-migraine":     "Headache",
  "parkinsons":            "Tremor",
  "pediatric-neurology":   "Seizure",
  "multiple-sclerosis":    "Vision issues",
  "dementia-alzheimers":   "Memory loss",
  "sciatica":              "Numbness",
  "peripheral-neuropathy": "Numbness",
  "head-injury-concussion": "Dizziness",
  "cervical-spondylosis":  "Numbness",
};

const SYMPTOM_TAGS = ["Headache", "Seizure", "Vision issues", "Numbness", "Weakness", "Memory loss", "Tremor", "Dizziness"];

export default function PatientDashboard() {
  const { user } = useAuth();

  // Pre-fill country from the user's saved profile (set at OTP signup).
  // Effect runs once when the user object arrives.
  useEffect(() => {
    if (user?.country && user.country !== form.country_code) {
      setForm((f) => ({ ...f, country_code: user.country }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.country]);
  const { t, lang } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [doctors, setDoctors] = useState([]);
  const [cases, setCases] = useState([]);
  const [files, setFiles] = useState([]);
  // Upload state — multi-file with per-file progress.
  // upload queue: [{id, name, pct, status: 'pending'|'uploading'|'done'|'error', error?}]
  const [uploadQueue, setUploadQueue] = useState([]);
  const [extractBusy, setExtractBusy] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pricing, setPricing] = useState({ standard: 1000, urgent: 1000, video: 2000 });
  const [freeConsult, setFreeConsult] = useState(null); // {eligible, used, expiry, remaining_days, source, case_id}
  const [showMetaActivated, setShowMetaActivated] = useState(false);

  // Wizard state — 1: pick doctor · 2: case details · 3: payment
  const [step, setStep] = useState(1);
  const [doctorQuery, setDoctorQuery] = useState("");
  const [specFilter, setSpecFilter] = useState("all");

  const [form, setForm] = useState({
    doctor_id: "",
    symptoms: "",
    duration_days: 7,
    language: "en",
    symptom_tags: [],
    priority: "standard",
    state: "Andhra Pradesh",
    // Family plan — who is this booking for?
    for_whom: "self",
    patient_profile_name: "",
    patient_profile_age: "",
    patient_profile_relation: "",
    patient_profile_sex: "",
    // Country picker → drives multi-tier pricing at checkout. Prefer the
    // saved user.country from OTP-signup; fall back to "IN".
    country_code: "IN",
  });
  const [consent, setConsent] = useState(false);
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailSavedAddr, setEmailSavedAddr] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [redFlag, setRedFlag] = useState(null);
  const [rfLoading, setRfLoading] = useState(false);
  const [pendingCid, setPendingCid] = useState(null); // set when booking created in step 3
  const [coupon, setCoupon] = useState({}); // { code, discount, original_fee, final_fee }
  const [paying, setPaying] = useState(false);

  const selectedDoctor = useMemo(
    () => doctors.find((d) => d.id === form.doctor_id) || null,
    [doctors, form.doctor_id]
  );

  const load = async () => {
    const [d, c, p] = await Promise.all([api.get("/doctors"), api.get("/consultations"), api.get("/pricing")]);
    setDoctors(d.data); setCases(c.data);
    setPricing({ standard: p.data.standard, urgent: p.data.urgent, video: p.data.video || 1500 });
    // Free-consult status (Meta Lead → ₹0 STANDARD second opinion, expires 30d).
    // Fire-and-forget: a 401/403 or endpoint hiccup should never break dashboard load.
    api.get("/patients/me/free-consult-status")
      .then((r) => {
        setFreeConsult(r.data);
        // First-time Meta activation modal (fires once per session after OTP verify).
        try {
          const flag = sessionStorage.getItem("noi_meta_activated");
          if (flag === "1" && r.data?.eligible && !r.data?.used) {
            setShowMetaActivated(true);
            sessionStorage.removeItem("noi_meta_activated");
          }
        } catch {}
      })
      .catch(() => setFreeConsult(null));
    const preselect = params.get("doctor");
    const urlPriority = params.get("priority");
    const urlTag = params.get("tag");
    // NeuroBot deeplink params:
    const urlSlug = params.get("slug");
    const urlSymptoms = params.get("symptoms");
    const symSession = params.get("sym_session");
    const updates = {};
    if (preselect && d.data.find((x) => x.id === preselect)) {
      updates.doctor_id = preselect;
    }
    if (urlPriority === "urgent" || urlPriority === "standard") {
      updates.priority = urlPriority;
    }
    if (urlTag) {
      updates.symptom_tags = [urlTag];
    } else if (urlSlug && SLUG_TO_TAG[urlSlug]) {
      updates.symptom_tags = [SLUG_TO_TAG[urlSlug]];
    }
    if (urlSymptoms) {
      updates.symptoms = urlSymptoms.slice(0, 600);
    }
    if (Object.keys(updates).length) {
      setForm((f) => ({ ...f, ...updates }));
      // Jump to step 2 if the quiz / NeuroBot gave us tag / priority / symptoms,
      // so users feel progress instead of starting from a blank doctor list.
      if (updates.doctor_id || updates.symptom_tags || updates.symptoms) {
        // If no doctor was picked, auto-pick the featured one.
        if (!updates.doctor_id) {
          const featured = d.data.find((x) => x.is_featured) || d.data[0];
          if (featured) setForm((f) => ({ ...f, ...updates, doctor_id: featured.id }));
        }
        setStep(2);
        // GA4 track conversion from NeuroBot
        if (symSession && typeof window !== "undefined" && window.gtag) {
          window.gtag("event", "neurobot_to_wizard", {
            sym_session: symSession,
            slug: urlSlug || null,
            priority: updates.priority || null,
          });
        }
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // GA4: fire booking_started once per mount (top of funnel).
  useEffect(() => { ga.bookingStarted({ step: 1 }); }, []);

  // GA4: fire booking_step_2 whenever the patient actually reaches Case Details.
  const reportedStep2 = useRef(false);
  useEffect(() => {
    if (step === 2 && !reportedStep2.current) {
      reportedStep2.current = true;
      ga.bookingStep2({
        priority: form.priority,
        has_prefill: !!params.get("sym_session"),
        tags: (form.symptom_tags || []).join(",").slice(0, 80),
      });
    }
  }, [step]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced red-flag detection (only when on step 2 and user typing)
  useEffect(() => {
    if (step !== 2) return;
    const text = (form.symptoms || "").trim();
    if (text.length < 12 && form.symptom_tags.length === 0) { setRedFlag(null); return; }
    const h = setTimeout(async () => {
      setRfLoading(true);
      try {
        const r = await api.post("/ai/red-flags", { symptoms: text, symptom_tags: form.symptom_tags });
        setRedFlag(r.data);
      } catch (err) { console.warn("[ai/red-flags]", err?.message || err); } finally { setRfLoading(false); }
    }, 800);
    return () => clearTimeout(h);
  }, [form.symptoms, form.symptom_tags, step]);

  // (UPI QR polling removed — Cashfree's drop-in handles UPI directly)

  // Upload a single File and update the per-file row in `uploadQueue` as it
  // progresses. Reused by both the dropzone and the legacy <input>.
  // For files ≥ 50 MB, uses the R2 multipart pipeline (direct browser →
  // Cloudflare R2 via presigned URLs, up to 5 GB, resumable). Small files
  // still use the legacy /files/upload path for zero-config compatibility.
  const uploadOneFile = async (f, rowId) => {
    const CHUNK_THRESHOLD = 50 * 1024 * 1024;
    const R2_CHUNK_SIZE   = 10 * 1024 * 1024;   // matches backend RECOMMENDED_CHUNK_SIZE
    const RESUME_KEY = (file) => `noi.r2.resume.${user?.id || "anon"}.${file.name}.${file.size}.${file.lastModified}`;
    const updateRow = (patch) =>
      setUploadQueue((q) => q.map((r) => (r.id === rowId ? { ...r, ...patch } : r)));

    // ---- Retry helper (exponential back-off + jitter) ----
    const retryable = async (fn, retries = 4) => {
      let lastErr;
      for (let attempt = 0; attempt <= retries; attempt++) {
        try { return await fn(); }
        catch (e) {
          lastErr = e;
          if (attempt === retries) break;
          await new Promise((r) => setTimeout(r, 800 * 2 ** attempt + Math.random() * 400));
        }
      }
      throw lastErr;
    };

    // ---- Direct-to-R2 multipart upload path (5 GB cap) ----
    const uploadR2 = async (file) => {
      let sessionId = null;
      let totalParts = 0;
      // Resume?
      const cached = localStorage.getItem(RESUME_KEY(file));
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          sessionId = parsed.id;
          totalParts = parsed.total_parts;
        } catch { localStorage.removeItem(RESUME_KEY(file)); }
      }
      if (!sessionId) {
        const init = await api.post("/patient/uploads/init", {
          filename: file.name,
          total_size: file.size,
          content_type: file.type || "application/octet-stream",
          purpose: "scan",
        });
        sessionId = init.data.upload_row_id;
        totalParts = init.data.total_parts;
        localStorage.setItem(RESUME_KEY(file), JSON.stringify({
          id: sessionId, total_parts: totalParts,
        }));
      }
      const partsMap = {};
      let doneCount = 0;
      updateRow({ pct: 5 });
      // 3-way parallelism for speed on mobile
      const queue = Array.from({ length: totalParts }, (_, i) => i + 1);
      let cursor = 0;
      const worker = async () => {
        while (cursor < queue.length) {
          const partNumber = queue[cursor++];
          const start = (partNumber - 1) * R2_CHUNK_SIZE;
          const end = Math.min(start + R2_CHUNK_SIZE, file.size);
          const blob = file.slice(start, end);
          const etag = await retryable(async () => {
            const s = await api.post(`/patient/uploads/sign-part/${sessionId}/${partNumber}`);
            const put = await fetch(s.data.url, { method: "PUT", body: blob });
            if (!put.ok) throw new Error(`R2 PUT ${put.status}`);
            const et = (put.headers.get("etag") || put.headers.get("ETag") || "").replaceAll('"', "");
            if (!et) throw new Error("missing etag");
            return et;
          });
          partsMap[partNumber] = etag;
          doneCount++;
          updateRow({ pct: 5 + Math.round((doneCount / totalParts) * 90) });
        }
      };
      await Promise.all([worker(), worker(), worker()]);
      const partsPayload = Object.entries(partsMap)
        .map(([pn, et]) => ({ PartNumber: Number(pn), ETag: et }))
        .sort((a, b) => a.PartNumber - b.PartNumber);
      const resp = await api.post(`/patient/uploads/complete/${sessionId}`,
                                    { parts: partsPayload });
      localStorage.removeItem(RESUME_KEY(file));
      return { data: resp.data.file };
    };
    const uploadChunked = async (file) => {
      // Legacy path — kept for reference only, no longer wired. R2 flow
      // above handles all files > CHUNK_THRESHOLD via multipart-to-R2.
      const totalChunks = Math.ceil(file.size / (5 * 1024 * 1024));
      throw new Error(`Legacy chunked upload retired; R2 handles ${totalChunks}-part uploads now.`);
    };

    try {
      updateRow({ status: "uploading", pct: 5 });
      let result;
      if (f.size > CHUNK_THRESHOLD) {
        // R2 pipeline — 5 GB cap, direct browser → Cloudflare R2,
        // no pod memory pressure, resumable.
        result = await uploadR2(f);
      } else {
        const fd = new FormData(); fd.append("file", f); fd.append("purpose", "scan");
        result = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setFiles((list) => [...list, result.data]);
      updateRow({ status: "done", pct: 100, file: result.data });
      return result.data;
    } catch (err) {
      updateRow({ status: "error", error: err.response?.data?.detail || "Upload failed" });
      return null;
    }
  };

  // Top-level entry: accepts a FileList (from dropzone or <input multiple>).
  const onUploadMany = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    // Seed queue rows so user sees their files appear immediately.
    const rows = arr.map((f, idx) => ({
      id: `up_${Date.now()}_${idx}`,
      name: f.name,
      size: f.size,
      pct: 0,
      status: "pending",
    }));
    setUploadQueue((q) => [...q, ...rows]);
    // Upload sequentially to avoid hammering the backend or hitting concurrency
    // limits on slower connections. Patients rarely upload >2-3 files anyway.
    for (let i = 0; i < arr.length; i++) {
      await uploadOneFile(arr[i], rows[i].id);
    }
  };

  // Legacy single-file path kept for any callers that still pass an event.
  const onUpload = async (e) => {
    const list = e.target.files;
    if (list?.length) await onUploadMany(list);
    e.target.value = "";
  };

  // Drag-and-drop handlers.
  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) onUploadMany(e.dataTransfer.files);
  };

  // AI extract — call once after at least one file is uploaded.
  const runExtract = async () => {
    const done = uploadQueue.find((r) => r.status === "done" && r.file);
    const target = done?.file || files[0];
    if (!target) return;
    setExtractBusy(true);
    setExtractResult(null);
    try {
      const r = await api.post("/ai/extract-case-details", { file_id: target.id });
      if (!r.data.ok) {
        toast.error(r.data.error || "AI couldn't read this file");
        setExtractResult({ ok: false, error: r.data.error });
        return;
      }
      // Auto-apply non-empty values to the form (patient profile + symptoms + priority + tag).
      const r2 = r.data;
      setForm((f) => {
        const next = { ...f };
        if (r2.findings || r2.impression) {
          const txt = [r2.findings, r2.impression].filter(Boolean).join(" ");
          if (txt && (!f.symptoms || f.symptoms.length < 20)) {
            next.symptoms = txt.slice(0, 600);
          }
        }
        if (r2.suggested_priority === "urgent" || r2.suggested_priority === "standard") {
          next.priority = r2.suggested_priority;
        }
        if (r2.suggested_condition_slug && r2.suggested_condition_slug !== "general" && SLUG_TO_TAG[r2.suggested_condition_slug]) {
          const tag = SLUG_TO_TAG[r2.suggested_condition_slug];
          if (!f.symptom_tags.includes(tag)) next.symptom_tags = [...f.symptom_tags, tag];
        }
        if (r2.patient_name && f.for_whom === "other" && !f.patient_profile_name) {
          next.patient_profile_name = r2.patient_name.slice(0, 80);
        }
        if (r2.age && f.for_whom === "other" && !f.patient_profile_age) {
          next.patient_profile_age = r2.age;
        }
        if (r2.sex && f.for_whom === "other" && !f.patient_profile_sex) {
          next.patient_profile_sex = r2.sex;
        }
        return next;
      });
      setExtractResult(r.data);
      toast.success("Case details auto-filled from your report");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI extract failed");
    } finally { setExtractBusy(false); }
  };

  const removeUploadedFile = async (id) => {
    setFiles((list) => list.filter((f) => f.id !== id));
    setUploadQueue((q) => q.filter((r) => r.file?.id !== id));
  };

  const toggleTag = (tag) =>
    setForm((f) => ({ ...f, symptom_tags: f.symptom_tags.includes(tag) ? f.symptom_tags.filter((x) => x !== tag) : [...f.symptom_tags, tag] }));

  // ---- Step transitions ----
  const pickDoctor = (d) => { setForm((f) => ({ ...f, doctor_id: d.id })); setStep(2); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const goToPayment = async () => {
    if (!form.symptoms.trim()) { toast.error("Please describe your symptoms"); return; }
    if (form.for_whom === "family" && !form.patient_profile_name.trim()) {
      toast.error("Please enter the patient's name"); return;
    }
    if (!consent) { toast.error("Please accept the consent to continue"); return; }
    // If we already have a pending consultation from this wizard, reuse it
    if (pendingCid) { setStep(3); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    try {
      const payload = {
        ...form,
        patient_profile_age: form.patient_profile_age ? Number(form.patient_profile_age) : null,
        // Pydantic rejects empty strings on Optional[Literal/enum] fields.
        // Coerce "" to null so the backend treats them as "not provided".
        patient_profile_sex: form.patient_profile_sex || null,
        patient_profile_relation: form.patient_profile_relation || null,
        patient_profile_name: form.patient_profile_name?.trim() || null,
        file_ids: files.map((f) => f.id),
        consent_accepted: true,
        whatsapp_opt_in: whatsappOptIn,
      };
      const r = await api.post("/consultations", payload);
      // Free Meta-Lead consult: no payment step, jump straight to thank-you.
      if (r.data?.is_free_consult) {
        toast.success("🎉 Congratulations! Your FREE NeuroOpinion Second Opinion has been activated.");
        setFreeConsult((fc) => fc ? { ...fc, used: true, eligible: false, case_id: r.data.id } : fc);
        navigate(`/patient/thank-you/${r.data.id}`);
        return;
      }
      setPendingCid(r.data.id);
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Could not create consultation"); }
  };

  // ---- Payment handlers (reused from before) ----
  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  // Cashfree JS SDK loader — served from cashfree.com. The SDK exposes a
  // `Cashfree(...)` constructor and a `cashfree.checkout({paymentSessionId})`
  // method that opens the Cashfree drop-in (UPI + Cards + Netbanking).
  const loadCashfreeScript = () => new Promise((resolve) => {
    if (window.Cashfree) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  // Gateway-agnostic confirm — works for both Cashfree and Razorpay because
  // the backend `/payments/confirm` sniffs the order_id prefix.
  const confirmAfterCheckout = async (cid, { order_id, payment_id, signature }) => {
    await api.post("/payments/confirm", {
      consultation_id: cid,
      razorpay_order_id: order_id,
      razorpay_payment_id: payment_id || "",
      razorpay_signature: signature || "",
    });
  };

  const payNow = async (cid = pendingCid) => {
    if (!cid) return;
    setPaying(true);
    try {
      const o = await api.post("/payments/order", { consultation_id: cid });
      const data = o.data;

      // --- PayPal (International USD) — redirect to /pay/{cid} PayPal page ---
      if (data.gateway === "paypal" && data.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }

      ga.paymentInitiated({
        consultation_id: cid,
        amount: data.fee_rupees,
        gateway: data.gateway || "mock",
        priority: form.priority,
      });

      // --- Cashfree path ---
      if (data.gateway === "cashfree") {
        const ok = await loadCashfreeScript();
        if (!ok) { toast.error("Could not load Cashfree"); return; }
        const cashfree = window.Cashfree({ mode: data.env === "prod" ? "production" : "sandbox" });
        const result = await cashfree.checkout({
          paymentSessionId: data.payment_session_id,
          redirectTarget: "_modal",
        });
        // result.error = user cancelled / failed; result.paymentDetails = success
        if (result?.error) {
          toast.error(result.error.message || "Payment failed"); return;
        }
        // Verify server-side (don't trust the client).
        try {
          await confirmAfterCheckout(cid, { order_id: data.order_id });
          toast.success(`Payment of ₹${data.fee_rupees} successful`);
          navigate(`/patient/thank-you/${cid}`);
        } catch (e) {
          toast.error(fmtErr(e.response?.data?.detail) || "Payment verification failed");
        }
        return;
      }

      // --- Razorpay path (legacy / fallback) ---
      if (!data.enabled) {
        await confirmAfterCheckout(cid, { order_id: data.order_id, payment_id: `pay_${Math.random().toString(36).slice(2, 14)}` });
        toast.success(`Payment of ₹${data.fee_rupees} successful (mock)`);
        navigate(`/patient/thank-you/${cid}`);
        return;
      }
      const okRzp = await loadRazorpayScript();
      if (!okRzp) { toast.error("Could not load Razorpay"); return; }
      const rzp = new window.Razorpay({
        key: data.key, amount: data.amount, currency: data.currency, order_id: data.order_id, name: data.name,
        description: "Neuro second opinion", prefill: data.prefill, theme: { color: "#1E40AF" },
        handler: async (res) => {
          try {
            await confirmAfterCheckout(cid, { order_id: res.razorpay_order_id, payment_id: res.razorpay_payment_id, signature: res.razorpay_signature });
            toast.success(`Payment of ₹${data.fee_rupees} successful`);
            navigate(`/patient/thank-you/${cid}`);
          } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Payment verification failed"); }
        },
        modal: { ondismiss: () => toast("Payment cancelled") },
      });
      rzp.on("payment.failed", (resp) => toast.error(resp?.error?.description || "Payment failed"));
      rzp.open();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Payment failed"); }
    finally { setPaying(false); }
  };

  const payViaUPI = async (cid = pendingCid) => {
    // Cashfree drop-in handles UPI natively inside the same checkout modal —
    // no need for a separate UPI-only path. Fall through to payNow which will
    // show UPI/Cards/Netbanking tabs in the Cashfree SDK.
    return payNow(cid);
  };

  // UPI QR flow removed — Cashfree drop-in handles UPI natively (PhonePe, GPay, Paytm).

  // ---------- Filtered doctors ----------
  const specs = useMemo(() => Array.from(new Set(doctors.map((d) => d.specialization).filter(Boolean))), [doctors]);
  const filteredDoctors = useMemo(() => {
    const q = doctorQuery.trim().toLowerCase();
    return doctors.filter((d) => {
      if (specFilter !== "all" && d.specialization !== specFilter) return false;
      if (q && !(d.name || "").toLowerCase().includes(q) && !(d.specialization || "").toLowerCase().includes(q) && !(d.bio || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [doctors, doctorQuery, specFilter]);

  // Flat platform pricing — Regular ₹1,000 / Emergency ₹2,000 / Video follow-up ₹2,000
  // (same fees for every doctor — set in /api/pricing).
  // Country-aware fee for the sidebar/CTA labels. Backend is still the
  // authoritative source at consultation-create time — this is display-only.
  const africaCodes = ["NG","KE","ZA","GH","EG","TZ","UG","ET","MA","DZ","TN","SN","CI","CM","AO","MZ","MG","RW"];
  const isIndia = form.country_code === "IN";
  const isAfrica = africaCodes.includes(form.country_code);
  const feeAmount = isIndia
    ? (form.priority === "urgent" ? 2000 : 1000)
    : (isAfrica
        ? (form.priority === "urgent" ?  100 :   50)
        : (form.priority === "urgent" ?  150 :  100));
  const feeSymbol = isIndia ? "₹" : "$";
  const feeSuffix = isIndia ? "" : " USD";
  const feeDisplay = `${feeSymbol}${feeAmount}${feeSuffix}`;

  // If patient is not from India, clear the Indian state pre-fill so a UK
  // patient's summary doesn't show "Andhra Pradesh". Priority (Regular vs
  // Emergency) is preserved because international patients now also have
  // both tiers ($50/$100 Africa · $100/$150 Others).
  useEffect(() => {
    if (!isIndia) {
      if (form.state && INDIAN_STATES.some((s) => s.name === form.state)) {
        setForm((f) => ({ ...f, state: "" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIndia]);
  const docStdFee = pricing.standard;
  const docUrgFee = pricing.urgent;
  const docVidFee = pricing.video || 1500;
  const currentFee = feeAmount; // country-aware amount for display labels

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/30 via-slate-50 to-white">
      <Navbar />

      {/* Premium hero banner — gradient, mini stats, breadcrumb */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-teal-900 border-b border-slate-200">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-teal-500/20 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-blue-500/15 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:24px_24px] pointer-events-none" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-semibold tracking-wide text-teal-200 backdrop-blur-sm">
                <ShieldCheck className="w-3 h-3"/> Encrypted &amp; private patient portal
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-3 tracking-tight">
                Hello, {user?.name?.split(" ")[0] || "there"}.
              </h1>
              <p className="text-sm sm:text-base text-white/70 mt-1.5">Book a verified neuro second opinion in three premium steps.</p>
            </div>
            <div className="hidden sm:flex items-center gap-5 text-white/80">
              <div className="text-right">
                <div className="font-heading text-lg font-bold">48 hr</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">Median report</div>
              </div>
              <div className="h-10 w-px bg-white/20" />
              <div className="text-right">
                <div className="font-heading text-lg font-bold">NIMHANS · AIIMS</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">Alumni only</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Free-consult status card (only for Meta Lead-matched patients) */}
        {freeConsult && (freeConsult.eligible || freeConsult.used) && (
          <div
            className={`mb-6 rounded-2xl border-2 p-4 sm:p-5 flex items-start gap-3 ${
              freeConsult.eligible
                ? "border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50"
                : "border-slate-200 bg-slate-50"
            }`}
            data-testid="free-consult-status-card"
          >
            <div className={`shrink-0 h-10 w-10 rounded-full grid place-items-center ${
              freeConsult.eligible ? "bg-emerald-600" : "bg-slate-400"
            }`}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              {freeConsult.eligible ? (
                <>
                  <div className="font-heading text-lg font-bold text-emerald-900">
                    🎉 Your FREE Second Opinion is unlocked
                  </div>
                  <div className="text-sm text-emerald-800 mt-0.5">
                    Book a <b>Written opinion (Standard)</b> below to redeem — you'll pay ₹0.
                    {typeof freeConsult.remaining_days === "number" && (
                      <> Offer expires in <b>{freeConsult.remaining_days} day{freeConsult.remaining_days === 1 ? "" : "s"}</b>.</>
                    )}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-emerald-700/70 mt-1 font-semibold">
                    Meta Lead offer · One-time use
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => { setStep(1); window.scrollTo({ top: document.getElementById("booking-wizard-anchor")?.offsetTop || 400, behavior: "smooth" }); }}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm"
                      data-testid="upload-reports-cta"
                    >
                      Upload Medical Reports <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="font-heading text-base font-bold text-slate-700">
                    FREE Second Opinion — Already Used
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5">
                    You've redeemed your one free consultation. Future consults use standard pricing.
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Refer & Earn — patient-facing tile.  Hydrates from
            /api/patients/me/referral and offers a one-tap WhatsApp share
            + copy-link with the patient's own 6-char code baked in. */}
        <ReferralTile />

        <div id="booking-wizard-anchor" />
        {/* Meta Lead first-time activation modal (fires once after OTP-verify). */}
        {showMetaActivated && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[60] grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4"
            onClick={() => setShowMetaActivated(false)}
            data-testid="meta-activated-modal"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl bg-white shadow-2xl border-2 border-emerald-200 overflow-hidden"
            >
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-6 py-8 text-center text-white">
                <div className="mx-auto h-14 w-14 rounded-full bg-white/25 grid place-items-center text-3xl">🎉</div>
                <div className="mt-4 font-heading text-2xl font-bold leading-tight">
                  Welcome to NeuroOpinion!
                </div>
                <div className="mt-1.5 text-sm text-white/90">
                  Your FREE Expert Brain & Spine Second Opinion has been activated.
                </div>
              </div>
              <div className="px-6 py-5">
                <div className="text-sm text-slate-700">
                  Please upload your <b>MRI, CT, X-rays and previous medical records</b> so our expert team can begin reviewing your case.
                </div>
                <div className="mt-5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowMetaActivated(false); setStep(1); window.scrollTo({ top: document.getElementById("booking-wizard-anchor")?.offsetTop || 400, behavior: "smooth" }); }}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm"
                    data-testid="meta-activated-cta"
                  >
                    Upload Medical Reports <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowMetaActivated(false)}
                    className="px-3 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50"
                    data-testid="meta-activated-close"
                  >
                    Later
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <Stepper step={step} onGoBack={(s) => setStep(s)} hasDoctor={!!form.doctor_id} canPay={!!pendingCid} />

        {/* ---------------- STEP 1 ---------------- */}
        {step === 1 && (
          <div className="mt-8" data-testid="wizard-step-1">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">Step 1 of 3</div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-1">Choose your specialist</h2>
                <p className="text-sm text-slate-500 mt-1">All doctors are MCI / NMC verified — pick one to continue.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    className="pl-9 h-11 w-full sm:w-64 border-slate-300 rounded-full bg-white"
                    placeholder="Search by name, specialization…"
                    value={doctorQuery}
                    onChange={(e) => setDoctorQuery(e.target.value)}
                    data-testid="doctor-search"
                  />
                </div>
                <Select value={specFilter} onValueChange={setSpecFilter}>
                  <SelectTrigger className="h-11 w-full sm:w-52 border-slate-300 rounded-full bg-white" data-testid="specialization-filter">
                    <SelectValue placeholder="All specializations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All specializations</SelectItem>
                    {specs.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="doctor-grid">
              {filteredDoctors.length === 0 && (
                <div className="col-span-full rounded-2xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500 bg-white">
                  No doctors match your search.
                </div>
              )}
              {filteredDoctors.map((d) => (
                <DoctorCard key={d.id} doctor={d} onPick={() => pickDoctor(d)} />
              ))}
            </div>
          </div>
        )}

        {/* ---------------- STEP 2 ---------------- */}
        {step === 2 && selectedDoctor && (
          <div className="mt-8" data-testid="wizard-step-2">
            <div className="mb-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">Step 2 of 3</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-1">Tell us about your case</h2>
              <p className="text-sm text-slate-500 mt-1">Symptoms, scans &amp; preferences — all encrypted, all confidential.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
              <Card className="p-6 sm:p-7 border-slate-200 bg-white rounded-2xl shadow-sm order-2 lg:order-1">
                <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <span className="w-9 h-9 rounded-lg bg-teal-50 border border-teal-100 grid place-items-center text-teal-700">
                    <Stethoscope className="w-4.5 h-4.5" />
                  </span>
                  Case details

                </h2>

              {/* Country picker → drives payment amount at checkout.
                  If patient set country during OTP signup, we LOCK it here
                  so they can't switch to India ₹1,000 after picking a
                  doctor at $100 USD (price arbitrage prevention).
                  India = ₹1,000 (Regular) / ₹2,000 (Emergency);
                  Africa = $50 USD; Others = $100 USD. */}
              <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="country-picker-block">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Your country</div>
                  {user?.country && (
                    <div className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid="country-locked-chip">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/> Locked from signup
                    </div>
                  )}
                </div>
                <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                  {user?.country ? (
                    <div className="flex-1 h-10 flex items-center rounded-lg border border-slate-200 bg-white/60 px-3 text-sm text-slate-800" data-testid="country-locked-display">
                      {(() => {
                        const c = countryByIso(form.country_code);
                        return `${c.name} (${c.dial})`;
                      })()}
                    </div>
                  ) : (
                    <select
                      value={form.country_code}
                      onChange={(e) => setForm({ ...form, country_code: e.target.value })}
                      className="flex-1 h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0B5ED7]"
                      data-testid="country-select"
                    >
                      {ALL_COUNTRIES.map((c) => (
                        <option key={c.iso} value={c.iso}>{c.name} ({c.dial})</option>
                      ))}
                    </select>
                  )}
                  <div className="text-sm font-semibold text-emerald-700 whitespace-nowrap" data-testid="live-fee">
                    You pay {feePreview(form.country_code, form.priority)}
                  </div>
                </div>
                <div className="mt-1.5 text-[11px] text-slate-500">
                  {user?.country
                    ? "Country was set when you signed up — it drives your pricing tier."
                    : "Pricing adapts to your country — no picking on payment."}
                </div>
              </div>

              {/* Family plan — for whom? */}
              <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Who is this for?</div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {[
                    { k: "self",   label: "Myself" },
                    { k: "family", label: "A family member" },
                  ].map((o) => (
                    <button
                      key={o.k}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, for_whom: o.k }))}
                      className={
                        "h-10 rounded-lg text-sm font-medium border transition " +
                        (form.for_whom === o.k
                          ? "bg-[#0B5ED7] text-white border-[#0B5ED7]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300")
                      }
                      data-testid={`for-whom-${o.k}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {form.for_whom === "family" && (
                  <div className="mt-3 grid grid-cols-2 gap-2" data-testid="family-fields">
                    <Input
                      placeholder="Full name"
                      value={form.patient_profile_name}
                      onChange={(e) => setForm({ ...form, patient_profile_name: e.target.value })}
                      className="bg-white"
                      data-testid="family-name"
                    />
                    <Input
                      type="number" min="0" max="120"
                      placeholder="Age"
                      value={form.patient_profile_age}
                      onChange={(e) => setForm({ ...form, patient_profile_age: e.target.value })}
                      className="bg-white"
                      data-testid="family-age"
                    />
                    <Select
                      value={form.patient_profile_relation}
                      onValueChange={(v) => setForm({ ...form, patient_profile_relation: v })}
                    >
                      <SelectTrigger className="bg-white" data-testid="family-relation"><SelectValue placeholder="Relation"/></SelectTrigger>
                      <SelectContent>
                        {["father","mother","spouse","son","daughter","brother","sister","other"].map((r) => (
                          <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={form.patient_profile_sex}
                      onValueChange={(v) => setForm({ ...form, patient_profile_sex: v })}
                    >
                      <SelectTrigger className="bg-white" data-testid="family-sex"><SelectValue placeholder="Sex"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Male</SelectItem>
                        <SelectItem value="F">Female</SelectItem>
                        <SelectItem value="O">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="mt-5 space-y-5">
                {params.get("sym_session") && form.symptoms && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50/70 px-4 py-3 flex items-start gap-3" data-testid="from-neurobot-banner">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-700 to-teal-600 text-white grid place-items-center text-xs">
                      ✨
                    </span>
                    <div className="text-xs text-blue-900 leading-relaxed flex-1">
                      <span className="font-semibold">Pre-filled from NeuroBot.</span>{" "}
                      Edit anything below or add more detail — the doctor reviews everything.
                    </div>
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Label>Symptoms</Label>
                    <VoiceDictationButton
                      value={form.symptoms}
                      onAppend={(v) => setForm({ ...form, symptoms: v })}
                      langCode={lang}
                      testId="symptoms-voice-btn"
                    />
                  </div>
                  <Textarea rows={4} placeholder="Describe what you're experiencing… or tap the mic to speak in your language."
                    value={form.symptoms} onChange={(e) => setForm({ ...form, symptoms: e.target.value })}
                    data-testid="symptoms-textarea" />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SYMPTOM_TAGS.map((tag) => (
                      <button key={tag} type="button" onClick={() => toggleTag(tag)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${form.symptom_tags.includes(tag) ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                        data-testid={`tag-${tag.replace(/\s/g, "-").toLowerCase()}`}>
                        {tag}
                      </button>
                    ))}
                  </div>
                  {redFlag && (redFlag.severity === "high" || redFlag.is_emergency) && (
                    <div className="mt-3 rounded-xl border-2 border-red-300 bg-red-50 p-4 animate-[pulse_1.8s_ease-in-out_infinite]" data-testid="rf-emergency-banner">
                      <div className="flex items-start gap-3">
                        <Zap className="w-5 h-5 text-red-700 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <div className="font-heading font-bold text-red-800 text-sm uppercase tracking-wider">⚠ Possible emergency detected</div>
                          <div className="mt-1 text-sm text-red-900">{redFlag.advice}</div>
                          {redFlag.flags?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {redFlag.flags.slice(0, 6).map((f, i) => (<span key={i} className="text-[11px] font-semibold bg-white text-red-700 border border-red-200 px-2 py-0.5 rounded-full">{f}</span>))}
                            </div>
                          )}
                          <div className="mt-3 flex flex-wrap gap-2">
                            <a href="tel:108" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-700 text-white text-xs font-semibold hover:bg-red-800" data-testid="rf-call-108">Call 108</a>
                            <a href="tel:102" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-700 text-white text-xs font-semibold hover:bg-red-800" data-testid="rf-call-102">Call 102</a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {redFlag && redFlag.severity === "medium" && !redFlag.is_emergency && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="rf-warning-banner">
                      <div className="flex items-start gap-2">
                        <Zap className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                        <div className="text-xs text-amber-900"><b>Worth checking urgently:</b> {redFlag.advice}</div>
                      </div>
                    </div>
                  )}
                  {rfLoading && <div className="mt-2 text-[11px] text-slate-400" data-testid="rf-loading">AI checking for red flags…</div>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Duration (days)</Label>
                    <Input type="number" min={0} value={form.duration_days}
                      onChange={(e) => setForm({ ...form, duration_days: Number(e.target.value) })}
                      data-testid="duration-input" />
                  </div>
                  <div>
                    <Label>{isIndia ? "Your state / UT" : "Your state / province / region"}</Label>
                    {isIndia ? (
                      <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                        <SelectTrigger data-testid="state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400">States</div>
                          {INDIAN_STATES.filter((s) => s.type === "state").map((s) => (<SelectItem key={s.code} value={s.name}>{s.name}</SelectItem>))}
                          <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 border-t border-slate-100 mt-1">Union territories</div>
                          {INDIAN_STATES.filter((s) => s.type === "ut").map((s) => (<SelectItem key={s.code} value={s.name}>{s.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder="e.g. London, California, Dubai"
                        value={form.state || ""}
                        onChange={(e) => setForm({ ...form, state: e.target.value })}
                        data-testid="state-input-free"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <Label>Preferred language for the opinion report</Label>
                  <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                    <SelectTrigger data-testid="language-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {INDIAN_LANGUAGES.map((l) => (
                        <SelectItem key={l.code} value={l.code}>
                          <span className="inline-flex items-center gap-2">
                            <span>{l.name}</span>
                            {l.native !== l.name && <span className="text-slate-400">· {l.native}</span>}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Consultation type</Label>
                  {freeConsult?.eligible && (
                    <div className="mt-2 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 flex items-start gap-2" data-testid="free-consult-banner">
                      <Sparkles className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                      <div className="text-sm text-emerald-900">
                        <div className="font-semibold">🎉 Your FREE Second Opinion is active</div>
                        <div className="text-xs mt-0.5">
                          Select <b>Written opinion</b> below to redeem — you'll pay ₹0.
                          Expires in {freeConsult.remaining_days ?? 30} days · Meta Lead offer.
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    {(() => {
                      const regularFee   = isIndia ? 1000 : (isAfrica ?  50 : 100);
                      const emergencyFee = isIndia ? 2000 : (isAfrica ? 100 : 150);
                      const cur          = isIndia ? "INR" : "USD";
                      return [
                        { v: "standard", title: "Regular opinion",   sla: "Within 48 hrs", fee: regularFee,   currency: cur, desc: "Detailed written second opinion by a specialist" },
                        { v: "urgent",   title: "Emergency opinion", sla: "Within 12 hrs", fee: emergencyFee, currency: cur, desc: "Priority queue · faster review" },
                      ];
                    })().map((opt) => {
                      const active = form.priority === opt.v;
                      const isFreeForThis = freeConsult?.eligible && opt.v === "standard" && isIndia;
                      return (
                        <button key={opt.v} type="button" onClick={() => setForm({ ...form, priority: opt.v })}
                          className={`text-left p-4 rounded-xl border-2 transition ${active ? "border-blue-700 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                          data-testid={`priority-${opt.v}`}>
                          <div className="flex items-center justify-between">
                            <div className="font-semibold text-slate-900 flex items-center gap-2">
                              {opt.title}
                            </div>
                            <div className="text-xs font-semibold text-slate-500">{opt.sla}</div>
                          </div>
                          <div className="mt-1 text-xs text-slate-500">{opt.desc}</div>
                          {isFreeForThis ? (
                            <div className="mt-2 flex items-center gap-2">
                              <span className="font-heading text-lg font-bold text-emerald-600">FREE</span>
                              <span className="text-xs text-slate-400 line-through">₹{opt.fee}</span>
                            </div>
                          ) : (
                            <div className="mt-2 font-heading text-lg font-bold text-blue-700">
                              {opt.currency === "USD" ? `$${opt.fee} USD` : `₹${opt.fee}`}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <Label>Upload scans / reports</Label>
                    {files.length > 0 && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={runExtract}
                        disabled={extractBusy}
                        className="h-8 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-xs"
                        data-testid="ai-extract-btn"
                      >
                        <Sparkles className="w-3.5 h-3.5 mr-1"/>
                        {extractBusy ? "Reading report…" : "Auto-fill from report"}
                      </Button>
                    )}
                  </div>

                  {/* Drag-and-drop zone */}
                  <label
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    className={`block rounded-xl border-2 border-dashed px-5 py-6 sm:py-8 text-center cursor-pointer transition-all ${
                      isDragging
                        ? "border-blue-500 bg-blue-50/70 scale-[1.01]"
                        : "border-slate-300 bg-slate-50/40 hover:border-blue-400 hover:bg-blue-50/30"
                    }`}
                    data-testid="upload-dropzone"
                  >
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={onUpload}
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.dcm,.dicom,.dic,.mp4,.mov,.avi,.mpeg,.mpg,.webm,application/pdf,image/*,video/*,application/dicom"
                      data-testid="upload-scan-input"
                    />
                    <div className="flex flex-col items-center gap-1.5">
                      <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 grid place-items-center">
                        <Upload className="w-6 h-6"/>
                      </div>
                      <div className="font-heading font-semibold text-sm text-slate-900">
                        {isDragging ? "Drop your files here" : "Drop files here or tap to browse"}
                      </div>
                      <div className="text-[11px] text-slate-500 leading-relaxed max-w-sm">
                        MRI · CT · DICOM · PDF · JPG · MP4 · up to 5 GB each. Large studies stream directly to secure storage — resumable if your connection drops.<br/>
                        Multiple files OK — we'll auto-extract details from your report.
                      </div>
                    </div>
                  </label>

                  {/* Upload queue (per-file progress) */}
                  {uploadQueue.length > 0 && (
                    <div className="mt-3 space-y-2" data-testid="upload-queue">
                      {uploadQueue.map((r) => {
                        const isDicom = /\.(dcm|dicom|dic)$/i.test(r.name);
                        const isPdf = /\.pdf$/i.test(r.name);
                        const isImage = /\.(jpe?g|png|webp)$/i.test(r.name);
                        const isVideo = /\.(mp4|mov|avi|mpe?g|webm)$/i.test(r.name);
                        return (
                          <div key={r.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
                            r.status === "error" ? "border-rose-200 bg-rose-50" :
                            r.status === "done"  ? "border-emerald-200 bg-emerald-50" :
                                                   "border-slate-200 bg-white"
                          }`} data-testid={`upload-row-${r.id}`}>
                            <div className={`shrink-0 w-9 h-9 rounded-md grid place-items-center text-[10px] font-bold uppercase tracking-wider ${
                              isDicom ? "bg-purple-100 text-purple-700" :
                              isPdf   ? "bg-rose-100 text-rose-700" :
                              isImage ? "bg-blue-100 text-blue-700" :
                              isVideo ? "bg-amber-100 text-amber-700" :
                                        "bg-slate-100 text-slate-600"
                            }`}>
                              {isDicom ? "DCM" : isPdf ? "PDF" : isImage ? "IMG" : isVideo ? "VID" : "FILE"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-slate-900 truncate">{r.name}</div>
                              {r.status === "uploading" ? (
                                <>
                                  <div className="mt-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                                    <div className="h-full bg-blue-600 transition-all" style={{ width: `${r.pct}%` }} />
                                  </div>
                                  <div className="text-[10px] text-slate-500 mt-0.5">{r.pct}% uploaded</div>
                                </>
                              ) : r.status === "done" ? (
                                <div className="text-[10px] text-emerald-700 font-semibold">✓ Uploaded · {Math.round((r.size || 0) / 1024)} KB</div>
                              ) : r.status === "error" ? (
                                <div className="text-[10px] text-rose-700">{r.error}</div>
                              ) : (
                                <div className="text-[10px] text-slate-500">Queued…</div>
                              )}
                            </div>
                            {r.status === "done" && r.file && (
                              <button type="button"
                                      onClick={() => removeUploadedFile(r.file.id)}
                                      className="p-1 rounded hover:bg-rose-100 text-slate-400 hover:text-rose-600"
                                      aria-label="Remove file"
                                      data-testid={`remove-upload-${r.id}`}>
                                <X className="w-4 h-4"/>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {extractResult?.ok && (
                    <div className="mt-3 rounded-lg border border-purple-200 bg-purple-50/70 px-3 py-2.5 text-xs text-purple-900" data-testid="extract-result-banner">
                      <div className="font-semibold inline-flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5"/> Auto-filled from your report
                      </div>
                      {extractResult.findings && (
                        <div className="mt-1 leading-relaxed">{extractResult.findings.slice(0, 200)}</div>
                      )}
                    </div>
                  )}
                </div>

                <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 cursor-pointer hover:bg-slate-50" data-testid="consent-row">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-700 focus:ring-blue-600"
                    data-testid="consent-checkbox" />
                  <span className="text-xs text-slate-700 leading-relaxed">
                    I confirm I am 18+ (or the legal guardian of the patient), I have read and accept the{" "}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Privacy Policy</Link>,{" "}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Terms of Service</Link> and{" "}
                    <Link to="/disclaimer" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Medical Disclaimer</Link>,
                    and I explicitly consent to the processing of my sensitive health data for this second opinion (DPDP Act, 2023).
                  </span>
                </label>

                <label className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 cursor-pointer hover:bg-emerald-50" data-testid="whatsapp-optin-row">
                  <input type="checkbox" checked={whatsappOptIn}
                    onChange={(e) => setWhatsappOptIn(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-emerald-300 text-emerald-700 focus:ring-emerald-600"
                    data-testid="whatsapp-optin-checkbox" />
                  <span className="text-xs text-slate-700 leading-relaxed">
                    <b className="text-emerald-700">I agree to receive case updates on WhatsApp</b> &amp; SMS — payment receipts,
                    doctor-assigned alert and the final report download link.
                    Standard messaging rates from your operator may apply. You can opt out anytime by replying STOP.
                  </span>
                </label>

                {/* OTP-signed-up patients have a placeholder @otp.local email.
                    We let them add a real one so we can email the report. */}
                {(user?.email || "").toLowerCase().endsWith("@otp.local") && !emailSavedAddr ? (
                  <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid="email-collect-row">
                    <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-blue-700">Get your report by email too (optional)</label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="email"
                        value={emailDraft}
                        onChange={(e) => setEmailDraft(e.target.value)}
                        placeholder="you@example.com"
                        className="flex-1 h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="email-collect-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={emailSaving || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailDraft.trim())}
                        onClick={async () => {
                          setEmailSaving(true);
                          try {
                            const r = await api.put("/users/me/email", { email: emailDraft.trim() });
                            setEmailSavedAddr(r.data.email);
                            toast.success("Email saved — your report will arrive here too");
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Could not save email");
                          } finally {
                            setEmailSaving(false);
                          }
                        }}
                        className="h-10 sm:w-32"
                        data-testid="email-collect-save"
                      >
                        {emailSaving ? "Saving…" : "Save email"}
                      </Button>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      We'll deliver the doctor's report to both WhatsApp <b>and</b> this email — useful if you want a printable copy on a desktop.
                    </p>
                  </div>
                ) : null}
                {emailSavedAddr ? (
                  <div className="text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="email-collect-saved">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 13l4 4L19 7"/></svg>
                    Report will also be emailed to <b>{emailSavedAddr}</b>.
                  </div>
                ) : null}

                <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-slate-100">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="wizard-back-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Change doctor
                  </Button>
                  <Button onClick={goToPayment} disabled={!consent || !form.symptoms.trim()}
                    className="h-11 bg-blue-700 hover:bg-blue-800 disabled:opacity-50" data-testid="wizard-to-payment-btn">
                    Continue to payment · {feeDisplay} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
              </Card>

              {/* Sticky summary */}
              <div className="order-1 lg:order-2">
                <Card className="p-5 border-slate-200 bg-white rounded-2xl shadow-sm lg:sticky lg:top-20" data-testid="booking-summary">
                  <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-teal-700">Booking summary</div>
                  <div className="mt-3 flex items-start gap-3">
                    <img src={avatarFor(selectedDoctor)} alt={selectedDoctor.name} className="w-14 h-14 rounded-2xl object-cover bg-slate-100 ring-2 ring-teal-100" />
                    <div>
                      <div className="font-heading font-semibold text-slate-900 text-sm">{selectedDoctor.name}</div>
                      <div className="text-xs text-slate-600">{selectedDoctor.specialization}</div>
                      {selectedDoctor.rating > 0 && (
                        <div className="mt-0.5 text-xs text-amber-600 flex items-center gap-0.5"><Star className="w-3 h-3 fill-amber-500" /> {selectedDoctor.rating.toFixed(1)}</div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 border-t border-slate-100 pt-3 space-y-1.5 text-sm">
                    <Row label="Priority" val={form.priority === "video" ? "Video consult" : (form.priority === "urgent" ? "Emergency" : "Standard")} />
                    <Row label="Report language" val={INDIAN_LANGUAGES.find((l) => l.code === form.language)?.name || form.language} />
                    <Row label="Scans" val={files.length > 0 ? `${files.length} uploaded` : "None yet"} />
                  </div>
                  <div className="mt-4 border-t border-slate-100 pt-3 flex items-center justify-between">
                    <span className="text-xs text-slate-500">Total payable</span>
                    <span className="font-heading text-2xl font-bold text-slate-900" data-testid="total-payable">{feeDisplay}</span>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* ---------------- STEP 3 ---------------- */}
        {step === 3 && selectedDoctor && (
          <div className="mt-8" data-testid="wizard-step-3">
            <div className="mb-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">Step 3 of 3</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-1">Secure payment</h2>
              <p className="text-sm text-slate-500 mt-1">Powered by Cashfree · UPI · Cards · Net banking · 100% encrypted.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
              <div className="order-2 lg:order-1 space-y-6">
                {/* AI Analysis Preview — shown BEFORE payment to build trust */}
                <AIAnalysisPreview rf={redFlag} symptoms={form.symptoms} priority={form.priority} />

              <Card className="p-6 sm:p-7 border-slate-200 bg-white rounded-2xl shadow-sm">
                <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <span className="w-9 h-9 rounded-lg bg-teal-50 border border-teal-100 grid place-items-center text-teal-700">
                    <CreditCard className="w-4.5 h-4.5" />
                  </span>
                  Choose how to pay
                </h2>
                <p className="text-sm text-slate-500 mt-1.5">
                  All payments are secured. {isIndia ? "Cashfree · UPI, Cards, Netbanking" : "PayPal · Card or PayPal balance"}. {isIndia ? `₹${coupon.final_fee ?? currentFee}` : feeDisplay} is charged only after you confirm.
                </p>

                {/* Coupon code — applies the discount server-side */}
                <CouponInput
                  consultationId={pendingCid}
                  fee={currentFee}
                  applied={coupon}
                  onApplied={setCoupon}
                />

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <PayTile
                    icon={<Smartphone className="w-5 h-5" />}
                    title="UPI"
                    subtitle="GPay, PhonePe, Paytm, BHIM — 0% MDR"
                    onClick={() => payViaUPI()}
                    disabled={paying}
                    testid="pay-upi-btn"
                    highlight
                  />
                  <PayTile
                    icon={<CreditCard className="w-5 h-5" />}
                    title="Card / NetBanking"
                    subtitle="Visa, Mastercard, RuPay, 58 banks"
                    onClick={() => payNow()}
                    disabled={paying}
                    testid="pay-card-btn"
                  />
                </div>

                {/* Trust signals row */}
                <div className="mt-6 grid grid-cols-3 gap-3 pt-5 border-t border-slate-100">
                  <div className="flex flex-col items-center gap-1.5 text-center">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 grid place-items-center text-emerald-600">
                      <ShieldCheck className="w-4.5 h-4.5" />
                    </div>
                    <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">SSL secure</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5 text-center">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 grid place-items-center text-emerald-600">
                      <CheckCircle2 className="w-4.5 h-4.5" />
                    </div>
                    <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">Refundable</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5 text-center">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 grid place-items-center text-emerald-600">
                      <Clock className="w-4.5 h-4.5" />
                    </div>
                    <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">48-hr report</span>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <Button variant="outline" onClick={() => setStep(2)} data-testid="back-to-details-btn" className="rounded-full">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                </div>
              </Card>
              </div>

              <div className="order-1 lg:order-2">
                <Card className="p-5 border-slate-200 bg-white rounded-2xl shadow-sm lg:sticky lg:top-20">
                  <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-teal-700">Review &amp; pay</div>
                  <div className="mt-3 flex items-start gap-3">
                    <img src={avatarFor(selectedDoctor)} alt={selectedDoctor.name} className="w-14 h-14 rounded-2xl object-cover bg-slate-100 ring-2 ring-teal-100" />
                    <div>
                      <div className="font-heading font-semibold text-slate-900 text-sm">{selectedDoctor.name}</div>
                      <div className="text-xs text-slate-600">{selectedDoctor.specialization}</div>
                    </div>
                  </div>
                  <div className="mt-4 border-t border-slate-100 pt-3 space-y-1.5 text-sm">
                    <Row label="Priority" val={form.priority === "video" ? "Video consult" : (form.priority === "urgent" ? "Emergency" : "Standard")} />
                    <Row label="Report language" val={INDIAN_LANGUAGES.find((l) => l.code === form.language)?.name || form.language} />
                    <Row label="Scans" val={files.length > 0 ? `${files.length} uploaded` : "None"} />
                  </div>
                  <div className="mt-4 border-t border-slate-100 pt-3 flex items-center justify-between">
                    <span className="text-xs text-slate-500">Total payable</span>
                    <div className="text-right">
                      {coupon.discount > 0 && (
                        <div className="text-xs text-emerald-700 font-semibold">−₹{coupon.discount} ({coupon.code})</div>
                      )}
                      <span className="font-heading text-2xl font-bold text-slate-900">
                        {isIndia ? `₹${coupon.final_fee ?? currentFee}` : feeDisplay}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Cases list always visible below wizard */}
        <div className="mt-12">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl font-semibold text-slate-900">My cases</h2>
            <span className="text-xs text-slate-500">{cases.length} {cases.length === 1 ? "case" : "cases"}</span>
          </div>

          {cases.length === 0 ? (
            <div className="mt-4 text-sm text-slate-500">No consultations yet.</div>
          ) : (
            <div className="mt-6 relative">
              {/* Vertical timeline line */}
              <div className="absolute left-3 sm:left-4 top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#0B5ED7]/30 via-slate-200 to-slate-100"/>
              <div className="space-y-5">
                {cases.map((c) => (
                  <div key={c.id} className="relative pl-10 sm:pl-12" data-testid={`case-timeline-${c.id}`}>
                    {/* Dot on the timeline */}
                    <div className={
                      "absolute left-0 top-2 w-6 h-6 sm:w-8 sm:h-8 rounded-full grid place-items-center ring-4 ring-white shadow " +
                      (c.status === "completed" ? "bg-emerald-500"
                        : c.status === "report_ready" ? "bg-emerald-500"
                        : c.status === "under_review" ? "bg-blue-500"
                        : c.status === "assigned" || c.status === "paid" ? "bg-amber-500"
                        : "bg-slate-400")
                    }>
                      {c.priority === "urgent"
                        ? <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-white"/>
                        : <Stethoscope className="w-3 h-3 sm:w-4 sm:h-4 text-white"/>}
                    </div>
                    <Card className="p-5 border-slate-200 hover:shadow-md transition" data-testid={`case-card-${c.id}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div>
                          <div className="font-heading font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                            {c.priority === "urgent" && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" data-testid="urgent-badge">
                                <Zap className="w-3 h-3" />Emergency
                              </span>
                            )}
                            {c.doctor_name}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                            <Clock className="w-3 h-3"/> {new Date(c.created_at).toLocaleString()}
                          </div>
                        </div>
                        <StatusBadge status={c.status}/>
                      </div>
                      <p className="text-sm text-slate-600 mt-3 line-clamp-2">{c.symptoms}</p>
                      <CaseTracker case={c} />
                      <div className="mt-4 flex items-center gap-2 flex-wrap">
                        {c.payment_status === "unpaid" ? (
                          <Button size="sm" onClick={() => payNow(c.id)} className="bg-[#0B5ED7] hover:bg-[#084BB0]" data-testid={`pay-btn-${c.id}`}>
                            <CreditCard className="w-4 h-4 mr-1"/> Pay ₹{c.fee}
                          </Button>
                        ) : (
                          <Link to={`/case/${c.id}`}><Button size="sm" variant="outline" data-testid={`view-case-${c.id}`}>View case</Button></Link>
                        )}
                      </div>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Subcomponents ----------

function Stepper({ step, onGoBack, hasDoctor, canPay }) {
  const steps = [
    { n: 1, label: "Choose Doctor", sub: "Pick a specialist" },
    { n: 2, label: "Case Details",  sub: "Symptoms & scans"  },
    { n: 3, label: "Payment",       sub: "Secure checkout"   },
  ];
  const progressPct = step === 1 ? 0 : step === 2 ? 50 : 100;
  return (
    <div className="mt-2 mb-2" data-testid="wizard-stepper">
      {/* Progress rail */}
      <div className="relative h-1 rounded-full bg-slate-200 max-w-3xl mx-auto">
        <div
          className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-teal-500 to-teal-600 transition-all duration-500"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      {/* Step bubbles */}
      <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-4 max-w-3xl mx-auto">
        {steps.map((s) => {
          const active = step === s.n;
          const done = step > s.n;
          const clickable = (s.n === 1) || (s.n === 2 && hasDoctor) || (s.n === 3 && canPay);
          return (
            <button
              key={s.n}
              disabled={!clickable}
              onClick={() => clickable && onGoBack(s.n)}
              className={`group flex items-center gap-3 p-3 sm:p-4 rounded-xl border transition text-left ${
                active
                  ? "border-teal-500 bg-white shadow-md"
                  : done
                    ? "border-emerald-200 bg-emerald-50/50"
                    : "border-slate-200 bg-white/60"
              } ${!clickable ? "opacity-60 cursor-not-allowed" : "hover:shadow-sm cursor-pointer"}`}
              data-testid={`stepper-${s.n}`}
            >
              <span className={`shrink-0 w-9 h-9 rounded-full grid place-items-center font-bold text-sm transition ${
                done
                  ? "bg-emerald-600 text-white"
                  : active
                    ? "bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-500"
              }`}>
                {done ? <CheckCircle2 className="w-4.5 h-4.5"/> : s.n}
              </span>
              <div className="min-w-0">
                <div className={`text-sm font-semibold truncate ${active ? "text-slate-900" : done ? "text-emerald-800" : "text-slate-700"}`}>
                  {s.label}
                </div>
                <div className="text-[11px] text-slate-500 truncate hidden sm:block">{s.sub}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DoctorCard({ doctor, onPick }) {
  // Deterministic "available today" urgency hint — stable across renders so
  // users don't see the badge flicker, but varies between doctors to feel real.
  const slotsLeft = ((doctor.id || "").charCodeAt(0) % 5) + 2; // 2-6 slots
  const availIn = ((doctor.id || "").charCodeAt(1) % 4) + 1;   // 1-4 hrs
  return (
    <Card className="p-5 border-slate-200 bg-white hover:shadow-lg hover:border-teal-300 hover:-translate-y-0.5 transition-all duration-200 group relative overflow-hidden rounded-2xl" data-testid={`doctor-card-${doctor.id}`}>
      {/* Decorative gradient blob */}
      <div className="absolute -top-16 -right-16 w-32 h-32 rounded-full bg-teal-100/50 blur-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden />
      {/* Featured flag is used for ordering only — no visible Founder badge. */}
      <div className="flex items-start gap-3 relative">
        <div className="relative shrink-0">
          <img
            src={avatarFor(doctor)}
            alt={doctor.name}
            className="w-16 h-16 rounded-2xl object-cover ring-2 ring-teal-100 bg-slate-50"
          />
          <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 ring-2 ring-white grid place-items-center">
            <BadgeCheck className="w-3 h-3 text-white" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-slate-900 text-sm truncate">
            {doctor.name}
          </div>
          <div className="text-xs text-slate-600 truncate">{doctor.specialization}</div>
          {doctor.alma_mater && (
            <div className="text-[11px] text-teal-700 font-semibold mt-0.5 truncate" data-testid={`doctor-alma-${doctor.id}`}>
              {doctor.alma_mater} alumnus
            </div>
          )}
          <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
            {doctor.rating > 0 && (
              <span className="inline-flex items-center gap-0.5 text-amber-600 font-semibold">
                <Star className="w-3 h-3 fill-amber-500" /> {doctor.rating.toFixed(1)}
              </span>
            )}
            {typeof doctor.experience_years === "number" && doctor.experience_years > 0 && (
              <span className="inline-flex items-center gap-1">
                <Briefcase className="w-3 h-3" /> {doctor.experience_years}+ yrs
              </span>
            )}
            {typeof doctor.consultations_count === "number" && doctor.consultations_count > 0 && (
              <span className="inline-flex items-center gap-1 text-slate-600">
                <Users className="w-3 h-3" /> {doctor.consultations_count >= 1000
                  ? `${(doctor.consultations_count / 1000).toFixed(doctor.consultations_count >= 10000 ? 0 : 1)}K+`
                  : doctor.consultations_count.toLocaleString()}
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/> Available today
            </span>
            {slotsLeft <= 4 && (
              <span className="text-[10px] font-medium text-amber-700" title="Live slot availability">
                Only {slotsLeft} slots left
              </span>
            )}
          </div>
        </div>
      </div>
      {doctor.bio && <p className="mt-3 text-xs text-slate-600 line-clamp-2 relative">{doctor.bio}</p>}
      <div className="mt-4 flex items-center justify-between pt-3 border-t border-slate-100 relative">
        <div className="text-[11px] text-slate-500">Opinion in {availIn}h · Priced by your country</div>
        <Button size="sm" onClick={onPick} className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 shadow-sm" data-testid={`pick-doctor-${doctor.id}`}>
          Book <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
    </Card>
  );
}
function CouponInput({ consultationId, fee: _fee, applied, onApplied }) {
  // _fee is part of the public component API but currently used only for display
  // by the parent; kept for future "minimum order ₹X" hints.
  void _fee;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const isApplied = !!applied?.code;

  const apply = async () => {
    if (!code.trim() || !consultationId) return;
    setBusy(true);
    try {
      const r = await api.post("/coupons/validate", {
        consultation_id: consultationId,
        code: code.trim(),
      });
      onApplied(r.data);
      toast.success(`₹${r.data.discount} off applied`);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Invalid code");
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => {
    if (!consultationId) return;
    setBusy(true);
    try {
      await api.post("/coupons/clear", { consultation_id: consultationId, code: applied.code });
      onApplied({});
      setCode("");
    } finally { setBusy(false); }
  };

  if (isApplied) {
    return (
      <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between gap-3" data-testid="coupon-applied">
        <div className="text-sm">
          <div className="font-semibold text-emerald-800 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4"/> {applied.code} applied
          </div>
          <div className="text-xs text-emerald-700 mt-0.5">
            ₹{applied.discount} off · You pay <b>₹{applied.final_fee}</b> instead of ₹{applied.original_fee}
          </div>
        </div>
        <button onClick={clear} disabled={busy} className="text-xs font-semibold text-emerald-700 hover:underline" data-testid="coupon-remove-btn">
          Remove
        </button>
      </div>
    );
  }
  return (
    <div className="mt-4 flex items-stretch gap-2" data-testid="coupon-input-row">
      <input
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="Have a coupon? Enter code"
        disabled={busy}
        className="flex-1 h-10 px-3 rounded-md border border-slate-200 text-sm font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        data-testid="coupon-code-input"
        maxLength={40}
      />
      <Button onClick={apply} disabled={busy || !code.trim() || !consultationId} variant="outline" className="h-10 px-4" data-testid="coupon-apply-btn">
        {busy ? "…" : "Apply"}
      </Button>
    </div>
  );
}



function PayTile({ icon, title, subtitle, onClick, disabled, testid, highlight }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`text-left rounded-xl border-2 p-4 transition ${
        highlight ? "border-blue-600 bg-blue-50 hover:bg-blue-100" : "border-slate-200 bg-white hover:border-blue-300"
      } disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className={`w-10 h-10 rounded-lg grid place-items-center ${highlight ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}>
        {icon}
      </div>
      <div className="mt-3 font-heading font-semibold text-slate-900 text-sm">{title}</div>
      <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
      {highlight && <div className="mt-2 text-[10px] font-bold uppercase tracking-wider text-blue-700">Most popular</div>}
    </button>
  );
}

function Row({ label, val }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800 text-right">{val}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending_payment: { t: "Awaiting payment", c: "bg-amber-50 text-amber-700 border-amber-200" },
    awaiting_review: { t: "Submitted · awaiting doctor", c: "bg-sky-50 text-sky-700 border-sky-200" },
    under_review: { t: "Doctor is reviewing", c: "bg-blue-50 text-blue-700 border-blue-200" },
    completed: { t: "Report ready", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    refunded: { t: "Refunded", c: "bg-slate-50 text-slate-700 border-slate-200" },
  };
  const s = map[status] || { t: status, c: "bg-slate-50 text-slate-700 border-slate-200" };
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${s.c}`}>{s.t}</span>;
}

// ---------------------------------------------------------------------------
// ReferralTile — dedicated Refer & Earn card on the patient dashboard.
// Hydrates from /api/patients/me/referral. Auto-hides if the fetch fails
// (e.g. the patient just signed up and doesn't have a code yet — endpoint
// creates one on first call so failure is rare).
// ---------------------------------------------------------------------------
function ReferralTile() {
  const [ref, setRef] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    api.get("/patients/me/referral")
      .then((r) => setRef(r.data))
      .catch(() => setRef(null));
  }, []);

  if (!ref?.code) return null;
  const shareUrl = ref.link;
  const shareText = ref.share_text;

  const shareWA = () => {
    try { window.gtag?.("event", "referral_shared", { channel: "whatsapp", source: "patient_dashboard_tile", code: ref.code }); } catch (_e) { /* gtag optional */ }
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      try { window.gtag?.("event", "referral_shared", { channel: "copy", source: "patient_dashboard_tile", code: ref.code }); } catch (_e) { /* gtag optional */ }
      toast.success("Copied — paste anywhere");
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) { toast.error("Could not copy — try the WhatsApp button"); }
  };
  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "NeuroOpinion India", text: shareText, url: shareUrl });
        try { window.gtag?.("event", "referral_shared", { channel: "native", source: "patient_dashboard_tile", code: ref.code }); } catch (_e) { /* gtag optional */ }
      } catch (_e) { /* dismissed */ }
    } else copyLink();
  };

  return (
    <Card
      className="p-5 sm:p-6 border-emerald-200 bg-gradient-to-br from-white via-emerald-50/40 to-teal-50/40 rounded-2xl overflow-hidden relative"
      data-testid="referral-tile"
    >
      <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-emerald-200/30 blur-3xl pointer-events-none"/>
      <div className="relative flex items-start gap-4 flex-wrap sm:flex-nowrap">
        <span className="w-11 h-11 rounded-xl bg-emerald-500 text-white grid place-items-center shrink-0 shadow-md">
          <Users className="w-5 h-5"/>
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700">Refer &amp; earn</div>
          <div className="mt-0.5 font-heading text-lg sm:text-xl font-bold text-slate-900 leading-tight">
            Give ₹{ref.reward_rupees} · Get ₹{ref.reward_rupees}
          </div>
          <div className="mt-1 text-xs sm:text-sm text-slate-600">
            Your code: <span className="font-mono font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid="referral-code">{ref.code}</span>
            <span className="text-slate-500"> · min order ₹{ref.min_order_rupees}</span>
          </div>
          {ref.stats?.paid_referrals > 0 && (
            <div className="mt-1.5 text-[11px] font-semibold text-emerald-700" data-testid="referral-tile-stats">
              🎉 You&apos;ve earned ₹{ref.stats.total_earned} from {ref.stats.paid_referrals} paid referral{ref.stats.paid_referrals !== 1 ? "s" : ""}
              {ref.stats.pending_credit > 0 && <span className="text-amber-600"> · ₹{ref.stats.pending_credit} pending credit</span>}
            </div>
          )}
        </div>
      </div>

      <div className="relative mt-4 flex flex-wrap items-center gap-2">
        <Button
          onClick={shareWA}
          size="sm"
          className="h-9 bg-green-600 hover:bg-green-700 text-white gap-1.5"
          data-testid="referral-tile-share-wa"
        >
          Share on WhatsApp
        </Button>
        {typeof navigator !== "undefined" && navigator.share && (
          <Button onClick={shareNative} size="sm" variant="outline" className="h-9" data-testid="referral-tile-share-native">
            More apps
          </Button>
        )}
        <Button onClick={copyLink} size="sm" variant="outline" className="h-9" data-testid="referral-tile-copy">
          {copied ? "Copied ✓" : "Copy link"}
        </Button>
      </div>
    </Card>
  );
}

